import { computed, effect, signal } from '@angular/core';
import { catchError, EMPTY, map, of, repeat, ReplaySubject, skipWhile, Subscription, switchMap, take, takeUntil, tap, timer } from 'rxjs';
import { logger } from 'src/app/pbir/utils/logger';
import { ErrorDetails, ExecuteDataQueryElement, ExecuteDataQueryRequest, QueryStatus, ResultSetDefinition, SchemaFieldTargetDto, ValueDataType, VisualFieldDto } from 'src/app/services/api.services';
import { VisualFilter } from '../../../VisualFilter';
import { VisualDataRequest } from '../../VisualDataRequest';
import { VisualDataQuery, VisualDataQueryStatus } from '../../VisualDataSource';
import { DataQueryVisualDataSource } from '../DataQueryVisualDataSource';
import { ResourceManagerImpl } from './ResourceManagerImpl';
import { TableImpl } from './TableImpl';
import { getErrorDetails, getRepeatDelay } from './utils';

const LOGGER = logger('QueryImpl');
const DEFAULT_ABORT_WAIT_TIME = 2500;

export class QueryImpl implements VisualDataQuery {
  constructor(
    public readonly dataSource: DataQueryVisualDataSource,
    public readonly request: VisualDataRequest,
    public readonly debugName?: string) {
    this.execute();
  }

  get injector() { return this.dataSource.injector; }
  get cache() { return this.dataSource.cache; }
  get dataQueryService() { return this.dataSource.dataQueryService; }
  get httpClient() { return this.dataSource.httpClient; }

  readonly queryState = computed(() => ({
    status: this.status(),
    queryId: this.id(),
    elements: this.elements(),

    requestedAt: this.requestedAt(),
    completedAt: this.completedAt(),
    executingAt: this.executingAt(),
    error: this.error(),

    anyResourcesLoading: this.resources.anyLoading(),

    tables: this.tables()?.map(t => ({
      resultSetIndex: t.resultSetIndex,
      resultSet: t.resultSet,
    }))
  }));

  private readonly __updateCachedQueryState = effect(() => {
    let queryState = this.queryState();
    this.cache.tryUpdateQuery(this.request, queryState);
  }, { injector: this.injector });

  readonly abortedSubject = new ReplaySubject<boolean>(1);

  readonly elements = signal<ReadonlyArray<ExecuteDataQueryElement>>(null);

  readonly resources = new ResourceManagerImpl();

  readonly status = signal<VisualDataQueryStatus>(VisualDataQueryStatus.Requested);
  readonly id = signal<string | undefined>(undefined);

  readonly requestedAt = signal<Date>(new Date());

  readonly executingAt = signal<Date | undefined>(undefined);
  readonly executing = computed(() => this.status() <= VisualDataQueryStatus.Executing);

  readonly completedAt = signal<Date | undefined>(undefined);
  readonly completed = computed(() => this.status() >= VisualDataQueryStatus.Success);
  readonly success = computed(() => this.status() == VisualDataQueryStatus.Success);
  readonly failure = computed(() => this.status() > VisualDataQueryStatus.Success);

  readonly error = signal<ErrorDetails | undefined>(undefined);

  readonly data = computed(() => {
    let tables = this.tables();
    return tables ? tables[0] : undefined;
  });

  readonly tables = signal<ReadonlyArray<TableImpl> | undefined>(undefined);



  private refCount = 0;
  private abortOnDrop: Subscription;

  addRef() {
    this.refCount++;

    if (this.abortOnDrop) {
      LOGGER.debug(`'${this.debugName}': ref increased, cancelling abort`, { query: this });

      this.abortOnDrop?.unsubscribe();
      this.abortOnDrop = null;
    }
  }

  dropRef(options?: { cancelImmediately: boolean; waitTime?: number; }) {
    if (this.refCount < 1) {
      LOGGER.error(`'${this.debugName}': Ref count is already zero`, { query: this });
      return;
    }

    this.refCount--;

    if (this.refCount < 1) {
      if (options?.cancelImmediately) {
        LOGGER.debug(`'${this.debugName}': ref dropped to 0, aborting`, { query: this });

        if (!this.completed()) {
          this.abort();
          this.resources.abortAll();
        }
      }
      else {
        LOGGER.debug(`'${this.debugName}': ref dropped to 0, aborting in ${DEFAULT_ABORT_WAIT_TIME}ms`, { query: this });

        this.abortOnDrop = timer(options?.waitTime ?? DEFAULT_ABORT_WAIT_TIME).subscribe(() => {
          if (this.refCount > 0)
            return;

          if (!this.completed()) {
            this.abort();
            this.resources.abortAll();
          }
        });
      }
    }
  }


  readonly aborted = signal<boolean>(false);

  abort(): void {
    if (this.aborted())
      return;

    if (!this.completed())
      this.cancelExecute();

    this.markAsAborted();
    this.resources.abortAll();

    this.fail({
      title: 'Aborted',
      detail: 'The query has been aborted',
    }, true);
  }

  private markAsAborted() {
    if (!this.aborted()) {
      this.aborted.set(true);
      this.abortedSubject.next(true);
    }
  }



  private execute() {
    let queryStartTime = performance.now();
    LOGGER.debug(
      `'${this.debugName}': execute`, { query: this, time: queryStartTime });

    // Random jitter in case multiple queries are fired
    timer(Math.random() * 100).pipe(
      switchMap(() => {
        this.requestedAt.set(new Date());
        this.status.set(VisualDataQueryStatus.Requested);

        let executeRequest = createExecuteDataQueryRequest(this.request);

        let executionCompleted = false;

        let startTime = performance.now();

        return this.dataSource.dataQueryService.executeQuery(executeRequest).pipe(
          tap(({ queryId, elements }) => {
            let endTime = performance.now();
            LOGGER.debug(
              `'${this.debugName}': executeQuery`,
              { query: this, queryId, elements, time: endTime, duration: (endTime - startTime) / 1000 });

            this.elements.set([...elements]);
            this.id.set(queryId);

            this.executingAt.set(new Date());
            this.status.set(VisualDataQueryStatus.Executing);
          }),

          switchMap(({ queryId, elements }) => {
            let startTime = performance.now();
            
            return this.dataSource.dataQueryService.getQueryState({ queryId }).pipe(
              repeat({ delay: n => timer(getRepeatDelay(n, { initial: 1500, max: 5000, jitter: 400 })) }),
              map(({ status, error }) => {
                let endTime = performance.now();
                LOGGER.debug(
                  `'${this.debugName}': getQueryState`,
                  { query: this, status, error, time: endTime, duration: (endTime - startTime) / 1000 });

                if (status == QueryStatus.Cancelled || status == QueryStatus.Expired || status == QueryStatus.Failed) {
                  executionCompleted = true;
                  this.fail(error);
                }
                else if (status == QueryStatus.Success) {
                  executionCompleted = true;
                }

                return { queryId, status, elements };
              }),
              skipWhile(_ => !executionCompleted),
              takeUntil(this.abortedSubject),
            );
          }),

          take(1),
          switchMap(({ queryId, elements }) => {
            if (this.aborted())
              return of(null);

            let startTime = performance.now();

            return this.dataSource.dataQueryService.getQueryResult({ queryId }).pipe(
              tap(({ resultSets }) => {
                let endTime = performance.now();
                LOGGER.debug(
                  `'${this.debugName}': getQueryResult`,
                  { query: this, resultSets, time: endTime, duration: (endTime - startTime) / 1000 });

                if (!resultSets?.length) {
                  LOGGER.warn(`'${this.debugName}': No result sets returned`, { query: this });
                }

                if (resultSets.length > 1) {
                  LOGGER.warn(`'${this.debugName}': More than one result set returned`, { query: this, resultSets });
                }

                this.tables.set(resultSets.map((rs, i) => new TableImpl(
                  this.request,
                  this.dataSource,
                  this.resources,
                  queryId,
                  elements,
                  i,
                  rs)
                ));

                endTime = performance.now();
                LOGGER.debug(
                  `'${this.debugName}': executeDone`,
                  { query: this, resultSets, time: endTime, duration: (endTime - queryStartTime) / 1000 });

                  this.succeed();
              }),
              takeUntil(this.abortedSubject),
            );
          }),

          takeUntil(this.abortedSubject),
        );
      }),

      catchError(err => {
        this.fail(err);
        return EMPTY;
      }),

      takeUntil(this.abortedSubject),
    ).subscribe();
  }


  private _cancelled = false;

  private cancelExecute() {
    if (!this._cancelled) {
      this._cancelled = true;

      let queryId = this.id();
      if (queryId) {
        this.dataSource.dataQueryService
          .cancelQuery({ queryId })
          .pipe(catchError(() => EMPTY))
          .subscribe();
      }
    }
  }


  private _completed = false;

  private complete() {
    if (this._completed)
      return;

    this._completed = true;
    this.completedAt.set(new Date());
  }


  private succeed(): void {
    LOGGER.debug(`'${this.debugName}': succeed`, { query: this });

    this.status.set(VisualDataQueryStatus.Success);
    this.complete();
  }

  private fail(reason: ErrorDetails, aborted?: boolean): void;
  private fail(reason: any, aborted?: boolean): void {
    if (this.status() == VisualDataQueryStatus.Failure)
      return;

    if (aborted) {
      LOGGER.debug(`'${this.debugName}': aborted`, { query: this, reason });
    }
    else {
      LOGGER.error(`'${this.debugName}': fail`, { query: this, reason });
    }

    this.cancelExecute();
    this.markAsAborted();

    let error = getErrorDetails(reason);
    this.error.set(error);

    this.status.set(VisualDataQueryStatus.Failure);
    this.complete();
  }
}

function createExecuteDataQueryRequest(request: VisualDataRequest): ExecuteDataQueryRequest {
  return {
    visualId: request.visualId,
    schema: request.schema.getSchemaTargetDto(),
    content: {
      visual: {
        fields: request.fields.map(f => <VisualFieldDto>{
          id: f.name,

          target: {
            ...f.target.schemaFieldTargetDto,
            visualId: request.visualId,
          },

          sorted: !!f.sorting,
          sortDirection: f.sorting?.sortDirection,
          sortOrder: f.sorting?.sortOrder,
        }),
        
        customFields: request.customFields.map(f => f.dto),
        currency: request.currency?.currencyCode,
      },

      filterGroups: request.filterGroups
        .map(g => ({
          operator: g.operator,
          filters: g.filters.map(x => VisualFilter.Predicate.createFilterDto(x))
        })),
    },

    userNow: new Date().toISOString(),

    recordPlan: request.recordPlan,
  };
}

export interface QueryState {
  readonly status: VisualDataQueryStatus;
  readonly queryId?: string;
  readonly elements?: ReadonlyArray<ExecuteDataQueryElement>;

  readonly requestedAt?: Date;
  readonly executingAt?: Date;
  readonly completedAt?: Date;
  readonly error?: any;

  readonly anyResourcesLoading?: boolean;

  readonly tables?: ReadonlyArray<QueryState.Table>;
}

export namespace QueryState {
  export interface Table {
    readonly resultSetIndex: number;
    readonly resultSet: ResultSetDefinition;
  }

  export interface TableColumn {
    readonly index: number;
    readonly target?: SchemaFieldTargetDto;
    readonly dataType: ValueDataType;
    readonly dataIndex: number;
    readonly identifier: string;
  }
}
